import { useTranslation } from 'react-i18next';
import { Tag } from '@bn-apps/design-system-ui-react';
import { ChevronDown, ChevronUp } from '@bn-apps/design-system-icons';
import type { ComboboxProps } from './Combobox.types';
import { useComboboxState } from './useComboboxState';
import { useComboboxKeyboard } from './useComboboxKeyboard';
import styles from './Combobox.module.scss';

export function Combobox({
  options,
  selectedValues,
  onSelectionChange,
  placeholder,
  label,
  id,
  disabled = false,
  className = '',
  maxDropdownHeight = '240px',
}: ComboboxProps) {
  const { t } = useTranslation();

  const state = useComboboxState({ options, selectedValues, onSelectionChange });

  useComboboxKeyboard({
    isOpen: state.isOpen,
    displayOptions: state.displayOptions,
    focusedIndex: state.focusedIndex,
    setFocusedIndex: state.setFocusedIndex,
    openDropdown: state.openDropdown,
    closeDropdown: state.closeDropdown,
    toggleOption: state.toggleOption,
    removeLastValue: state.removeLastValue,
    searchTerm: state.searchTerm,
    setSearchTerm: state.setSearchTerm,
    inputRef: state.inputRef,
    listRef: state.listRef,
  });

  const focusedOptionId =
    state.focusedIndex >= 0
      ? `${state.comboboxId}-option-${state.focusedIndex}`
      : undefined;

  const ChevronIcon = state.isOpen ? ChevronUp : ChevronDown;

  return (
    <div className={`${styles.root} ${className}`.trim()}>
      {label && (
        <label className={styles.label} htmlFor={id ?? state.comboboxId}>
          {label}
        </label>
      )}
      <div
        ref={state.containerRef}
        className={`${styles.container} ${disabled ? styles.disabled : ''}`.trim()}
        onClick={!disabled ? state.handleContainerClick : undefined}
      >
        <div className={styles.contentArea}>
          {selectedValues.map((selected) => (
            <Tag
              key={selected.value}
              text={selected.label}
              size="sm"
              color="blue"
              textWrap="nowrap"
              className={styles.fullWidthTag}
              onDismiss={!disabled ? () => state.removeValue(selected) : undefined}
              dismissAriaLabel={t('combobox.removeOption', { option: selected.label })}
            />
          ))}
          <input
            ref={state.inputRef}
            id={id ?? state.comboboxId}
            type="text"
            className={`${styles.input} ${selectedValues.length > 0 ? styles.inputOwnRow : ''}`.trim()}
            value={state.searchTerm}
            placeholder={placeholder ?? t('combobox.placeholder')}
            disabled={disabled}
            role="combobox"
            aria-expanded={state.isOpen}
            aria-autocomplete="list"
            autoComplete="off"
            aria-controls={state.listId}
            aria-activedescendant={focusedOptionId}
            onChange={state.handleInputChange}
            onFocus={state.handleInputFocus}
            onBlur={state.handleInputBlur}
          />
        </div>
        <div
          className={styles.chevronButton}
          data-chevron-button
          aria-hidden="true"
          onMouseDown={!disabled ? state.handleChevronMouseDown : undefined}
        >
          <ChevronIcon />
        </div>
      </div>

      {state.isOpen && (
        <ul
          ref={state.listRef}
          id={state.listId}
          className={styles.dropdown}
          role="listbox"
          aria-multiselectable="true"
          style={{ maxHeight: maxDropdownHeight }}
        >
          {state.displayOptions.length > 0 ? (
            state.displayOptions.map((option, index) => {
              const isSelected = state.isOptionSelected(option);
              const isFocused = state.focusedIndex === index;
              return (
                <li
                  key={option.value}
                  id={`${state.comboboxId}-option-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  className={`${styles.option} ${isFocused ? styles.focused : ''}`.trim()}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => state.toggleOption(option)}
                  onMouseEnter={() => state.setFocusedIndex(index)}
                >
                  <div className={`${styles.checkbox} ${isSelected ? styles.checked : ''}`.trim()}>
                    {isSelected && <div className={styles.checkmark} />}
                  </div>
                  <span className={styles.optionLabel}>{option.label}</span>
                </li>
              );
            })
          ) : (
            <li className={styles.noResults}>{t('combobox.noResults')}</li>
          )}
        </ul>
      )}
    </div>
  );
}
